import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

export default function DailyExpenseChart({ years, currency, selectedGlobalMonth, selectedGlobalYear }) {
  const [data, setData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(selectedGlobalMonth || new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!localStorage.getItem("userData")) {
      history.push("/login");
    }
  }, [history]);

  // Get userId, groupId
  const storedUserData = localStorage.getItem("userData");
  let groupId = "";
  if (storedUserData) {
    groupId = JSON.parse(storedUserData).groupId;
  }

  const monthsList = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  useEffect(() => {
    setSelectedMonth(selectedGlobalMonth);
  }, [selectedGlobalMonth]);

  useEffect(() => {
    setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
  }, [selectedGlobalYear]);

  useEffect(() => {
    if (!groupId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await apiService.getDailyExpenses(
          groupId,
          selectedYear,
          selectedMonth
        );
        setData(result);
      } catch (error) {
        console.error("Error fetching daily expenses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [groupId, selectedYear, selectedMonth]);

  const formatNumber = (value) => {
    return Number(value).toLocaleString();
  };

  return (
    <div className="shadow-lg rounded-lg bg-white dark:bg-gray-800 p-4 col-span-12 mb-4">
      {/* HEADER */}
      <div className="flex justify-between flex-col lg:flex-row mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
          Daily Expenses ({monthsList[selectedMonth - 1]} {selectedYear})
        </h2>
        <div className="flex gap-2 mt-2 lg:mt-0">
          <select
            className="text-xs text-gray-800 dark:text-white rounded-md px-3 py-2"
            style={{ minWidth: "100px", border: "solid #ddd 1px" }}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {monthsList.map((month, idx) => (
              <option key={month} value={idx + 1}>{month}</option>
            ))}
          </select>
          <select
            className="text-xs text-gray-800 dark:text-white rounded-md px-3 py-2"
            style={{ minWidth: "80px", border: "solid #ddd 1px" }}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>
      {/* CHART AREA */}
      {loading ? (
        <p className="text-center py-10 text-gray-600 dark:text-gray-300">
          Loading...
        </p>
      ) : (
        <div style={{ width: "100%", height: "350px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 20, right: 20, bottom: 50, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis
                dataKey="date"
                angle={-45}
                textAnchor="end"
                interval={0}
                dy={10}
                stroke="#888"
              />
              <YAxis 
                stroke="#888"
                tickFormatter={formatNumber}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    return (
                      <div className="bg-white p-2 border shadow rounded text-sm">
                        <p className="font-bold">{d.date}</p>
                        <p>Credit: {currency}{formatNumber(d.credit)}</p>
                        <p>Debit: {currency}{formatNumber(d.debit)}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Line type="monotone" dataKey="credit" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="debit" stroke="#ef4444" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}